import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import JumboDemoCard from "@jumbo/components/JumboDemoCard/JumboDemoCard";
import { GetUsersListApi } from "backendServices/ApiCalls";
import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";

/**
 * All Users — one row per account, with a View button that opens the full
 * record in a modal.
 *
 * A DataGrid row cannot carry ~40 columns legibly, so the grid shows only what
 * is needed to find a user and the modal carries everything else. Deliberately
 * a fresh page rather than an addition to Userlisttable.js, which is tangled up
 * with the legacy MLM binary-tree / payout / rank dialogs this report has no
 * use for.
 */

const DASH = "—";

/** Unix seconds, ISO strings and MySQL datetimes all appear in these columns. */
const formatDateTime = (value) => {
  if (value === null || value === undefined || value === "") return DASH;

  // trial_start / trial_end are unix seconds, stored as strings in some rows.
  const asNumber = Number(value);
  const date =
    Number.isFinite(asNumber) && String(value).trim() === String(asNumber)
      ? new Date(asNumber * 1000)
      : new Date(value);

  if (isNaN(date.getTime())) return DASH;
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDate = (value) => {
  if (!value) return DASH;
  const date = new Date(value);
  if (isNaN(date.getTime())) return DASH;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * `trial_status` is only updated when the user makes an authenticated request,
 * so an account that stops logging in keeps saying "Active" indefinitely. The
 * end date is the fact; the column is a stale cache. Always derive from
 * `trial_end`.
 */
const describeTrial = (row) => {
  if (!row?.trial || row.trial === "No") return { label: "No trial", color: "default" };

  const end = Number(row.trial_end);
  if (!Number.isFinite(end) || end <= 0) return { label: "No trial", color: "default" };

  const secondsLeft = end - Math.floor(Date.now() / 1000);
  const days = Math.abs(secondsLeft) / 86400;

  return secondsLeft > 0
    ? { label: `Trial — ${days.toFixed(1)}d left`, color: "info" }
    : { label: `Trial expired ${days.toFixed(1)}d ago`, color: "warning" };
};

const trialLengthDays = (row) => {
  const start = Number(row?.trial_start);
  const end = Number(row?.trial_end);
  if (!Number.isFinite(start) || !Number.isFinite(end) || start <= 0 || end <= start) {
    return DASH;
  }
  return `${Math.round((end - start) / 86400)} days`;
};

const Field = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
      {value === null || value === undefined || value === "" ? DASH : String(value)}
    </Typography>
  </Grid>
);

const Section = ({ title, children }) => (
  <>
    <Grid item xs={12}>
      <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 600 }}>
        {title}
      </Typography>
      <Divider />
    </Grid>
    {children}
  </>
);

function AllUsers() {
  const [userList, setUserList] = useState([]);
  const [selected, setSelected] = useState(null);

  const fetchUsers = () => {
    GetUsersListApi(
      (response) => {
        const rows = response?.data?.userdata || [];
        setUserList(rows.map((row, index) => ({ ...row, index: index + 1 })));
      },
      (error) => {
        console.log(error?.response?.data);
      }
    );
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const columns = [
    { field: "index", headerName: "#", width: 60 },
    { field: "email", headerName: "Email", width: 240 },
    { field: "firstname", headerName: "First Name", width: 130 },
    { field: "lastname", headerName: "Last Name", width: 130 },
    { field: "plan_pkg", headerName: "Plan", width: 110,
      valueGetter: (params) => params.row.plan_pkg || "Free" },
    {
      field: "trial_end",
      headerName: "Trial",
      width: 190,
      renderCell: (params) => {
        const { label, color } = describeTrial(params.row);
        return <Chip size="small" label={label} color={color} variant="outlined" />;
      },
    },
    {
      field: "emailstatus",
      headerName: "Email",
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value === "verified" ? "Verified" : "Unverified"}
          color={params.value === "verified" ? "success" : "error"}
          variant="outlined"
        />
      ),
    },
    {
      field: "lastlogin",
      headerName: "Last Login",
      width: 170,
      valueFormatter: (params) => formatDate(params.value),
    },
    { field: "login_status", headerName: "Status", width: 110 },
    {
      field: "actions",
      headerName: "Details",
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          startIcon={<VisibilityIcon />}
          onClick={() => setSelected(params.row)}
        >
          View
        </Button>
      ),
    },
  ];

  const user = selected;
  const trial = user ? describeTrial(user) : null;

  return (
    <Box p={2}>
      <JumboDemoCard
        title="All Users"
        wrapperSx={{ backgroundColor: "background.paper", pt: 0 }}
      >
        <Box sx={{ height: 600, width: 1 }}>
          <DataGrid
            initialState={{ pagination: { paginationModel: { pageSize: 12 } } }}
            rows={userList}
            getRowId={(row) => row.id}
            columns={columns}
            slots={{ toolbar: GridToolbar }}
            pageSizeOptions={[12, 24, 50, 100]}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
          />
        </Box>
      </JumboDemoCard>

      <Dialog
        open={Boolean(user)}
        onClose={() => setSelected(null)}
        maxWidth="md"
        fullWidth
      >
        {user && (
          <>
            <DialogTitle sx={{ pr: 6 }}>
              {[user.firstname, user.lastname].filter(Boolean).join(" ") || user.email}
              <Typography variant="body2" color="text.secondary">
                {user.email} · ID {user.id}
              </Typography>
              <IconButton
                onClick={() => setSelected(null)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Chip size="small" label={trial.label} color={trial.color} sx={{ mr: 1 }} />
                  <Chip
                    size="small"
                    label={user.emailstatus === "verified" ? "Email verified" : "Email unverified"}
                    color={user.emailstatus === "verified" ? "success" : "error"}
                    sx={{ mr: 1 }}
                  />
                  <Chip size="small" label={user.login_status || DASH} sx={{ mr: 1 }} />
                </Grid>

                <Section title="Account">
                  <Field label="User Name" value={user.username} />
                  <Field label="Email" value={user.email} />
                  <Field label="Mobile" value={user.mobile} />
                  <Field label="Language" value={user.language} />
                  <Field label="Company" value={user.company} />
                  <Field label="Affiliate Code" value={user.randomcode} />
                  <Field label="Signed Up" value={formatDateTime(user.createdat)} />
                  <Field label="Last Login" value={formatDateTime(user.lastlogin)} />
                  <Field label="Last IP" value={user.lastip} />
                  <Field label="Customer ID" value={user.customerid} />
                </Section>

                <Section title="Plan & Billing">
                  <Field label="Plan" value={user.plan_pkg || "Free"} />
                  <Field
                    label="Price"
                    value={
                      user.plan_price === null || user.plan_price === undefined
                        ? DASH
                        : `${user.currency || ""} ${user.plan_price}`.trim()
                    }
                  />
                  <Field label="Billing Type" value={user.sub_type} />
                  <Field label="Billing Period" value={user.plan_period} />
                  <Field label="Subscription Status" value={user.subscription_status} />
                  <Field label="Activated At" value={formatDateTime(user.activated_at)} />
                  <Field label="Next Billing" value={formatDateTime(user.next_billing_at)} />
                  <Field label="Plan Updated" value={formatDateTime(user.plan_updatedat)} />
                </Section>

                <Section title="Trial">
                  <Field label="On Trial" value={user.trial} />
                  <Field label="Trial Status (raw)" value={user.trial_status} />
                  <Field label="Trial Length" value={trialLengthDays(user)} />
                  <Field label="Computed" value={trial.label} />
                  <Field label="Trial Start" value={formatDateTime(user.trial_start)} />
                  <Field label="Trial End" value={formatDateTime(user.trial_end)} />
                </Section>

                <Section title="Address">
                  <Field label="Address" value={user.address1} />
                  <Field label="City" value={user.city} />
                  <Field label="Zip Code" value={user.zip_code} />
                  <Field label="Country" value={user.country} />
                </Section>

                <Section title="Referral">
                  <Field label="Sponsor" value={user.sponsor_name} />
                  <Field label="Sponsor Email" value={user.sponsor_email} />
                  <Field label="Is Affiliate" value={user.isAffiliate ? "Yes" : "No"} />
                  <Field label="Website / Reseller" value={user.website} />
                </Section>

                <Section title="Product Settings">
                  <Field label="Chat Active" value={user.isChatActive ? "Yes" : "No"} />
                  <Field label="Connection Type" value={user.connection_type} />
                  <Field label="Connect Status" value={user.connect_status} />
                  <Field label="CRM Status" value={user.crm_status} />
                  <Field label="Birthday Status" value={user.birthday_status} />
                  <Field label="Unfollow Status" value={user.unfollow_status} />
                </Section>
              </Grid>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}

export default AllUsers;
